import * as React from 'react';
import { Achievement, tierColors, categoryLabels } from '@/types/achievements';
import { AchievementBadge } from './AchievementBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';

interface AchievementCardProps {
  achievement: Achievement;
  unlocked?: boolean;
  unlockedAt?: string;
  progress?: { current: number; target: number };
  className?: string;
}

export const AchievementCard: React.FC<AchievementCardProps> = ({
  achievement,
  unlocked = false,
  unlockedAt,
  progress,
  className,
}) => {
  const progressPercent = progress
    ? Math.round((progress.current / progress.target) * 100)
    : unlocked
    ? 100
    : 0;

  return (
    <Card
      className={cn(
        'overflow-hidden transition-all duration-300 hover:shadow-md',
        unlocked ? 'border-primary/20' : 'border-border',
        className
      )}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <AchievementBadge
            achievement={achievement}
            unlocked={unlocked}
            size="md"
            progress={progressPercent}
          />
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3
                className={cn(
                  'font-semibold text-sm',
                  unlocked ? 'text-foreground' : 'text-muted-foreground'
                )}
              >
                {achievement.name}
              </h3>
              <Badge
                variant="outline"
                className="text-xs shrink-0"
                style={{
                  borderColor: tierColors[achievement.tier],
                  color: unlocked ? tierColors[achievement.tier] : 'hsl(var(--muted-foreground))',
                }}
              >
                {achievement.tier}
              </Badge>
            </div>

            <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
              {achievement.description}
            </p>

            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">
                {categoryLabels[achievement.category]}
              </span>
              <span className="font-medium text-primary">
                +{achievement.xp_reward} XP
              </span>
            </div>

            {progress && !unlocked && (
              <div className="mt-3">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground">Progress</span>
                  <span className="font-medium">
                    {progress.current}/{progress.target}
                  </span>
                </div>
                <Progress value={progressPercent} className="h-1.5" />
              </div>
            )}

            {unlocked && unlockedAt && (
              <p className="text-xs text-muted-foreground mt-2">
                Unlocked {new Date(unlockedAt).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
